import DOM from 'lib/DOM';
import Layer from 'components/Layer';
import PropTypes from 'prop-types';
import * as React from 'react';
import ReactDOM from 'react-dom';

/**
 * ## [Layer](/internal/components/Layer) with an un-styled arrow pointing to its trigger.
 * ### Styling is added when another component, like [Hovercard](/internal/components/Hovercard), composes `LayerWithArrow`.
 *
 * ```example
 * <LayerTrigger
 *   defaultLayerShown
 *   layer={
 *     <LayerWithArrow>
 *       LayerWithArrow
 *     </LayerWithArrow>
 *   }>
 *   <Button>Hover</Button>
 * </LayerTrigger>
 * ```
 *
 * ```example
 * <LayerTrigger
 *   defaultLayerShown
 *   layer={
 *     <Hovercard hideOnBlur={false} hideOnEscape={false}>
 *       <HovercardBody>
 *         Hovercard
 *       </HovercardBody>
 *     </Hovercard>
 *   }>
 *   <Button>Hover</Button>
 * </LayerTrigger>
 * ```
 */

type Position = {
  top?: number;
  left?: number;
};

export type Props = {
  placement?: 'top' | 'bottom' | 'left' | 'right';
  arrowClassName?: string;
  children: React.ReactNode;

  // These should really be all the props from Layer, but that isn't fully converted yet
  className: string;
};

// Context is always passed in from LayerTrigger, but Typescript doesn't know
// (because of cloneElement) so we make this optional to fix that.
export type InjectedProps = {
  context?: HTMLElement;
}

type State = {
  arrowPosition: Position;
};

class LayerWithArrow extends React.Component<Props & InjectedProps, State> {
  state = {
    arrowPosition: {},
  };

  componentDidMount() {
    if (this.props.arrowClassName !== null) {
      this.updateArrowPosition();
    }
  }

  componentWillReceiveProps() {
    if (this.props.arrowClassName !== null) {
      this.updateArrowPosition();
    }
  }

  updateArrowPosition() {
    const { context, placement } = this.props;
    if (!context) {
      return;
    }

    const contextPosition = DOM.getOffset(context);
    const node = ReactDOM.findDOMNode(this) as HTMLElement | null;
    if (!node) {
      return;
    }

    const nodePosition = DOM.getOffset(node);
    const position: Position = {};

    if (placement === 'bottom' || placement === 'top') {
      position.left = contextPosition.left + context.offsetWidth / 2 - nodePosition.left;
    } else if (placement === 'left' || placement === 'right') {
      position.top = contextPosition.top + context.offsetHeight / 2 - nodePosition.top;
    }

    this.setState({ arrowPosition: position });
  }

  render() {
    const { arrowClassName, children, ...others } = this.props;
    const { arrowPosition } = this.state;

    return (
      <Layer {...others}>
        {children}
        <div className={arrowClassName} style={arrowPosition} />
      </Layer>
    );
  }
}

export default LayerWithArrow;
